import edjsHTML from 'editorjs-html';
import { invariant } from 'ts-invariant';

import { TImgUrl } from '@boc/saleor-common/urls';

import { defaultNameGenerator, formatPrice, getBrand, productTypeMapping } from '.';
import { getAttributeValue } from '../products/attributes';
import { generateShortDescription } from '../products/description';
import type { WeightUnitsEnum } from '../saleor-graphql';
import type { TChannel, TProduct, TVariant } from './types';

const edjsParser = edjsHTML();

function convertToKg({ value, unit }: { value: number; unit: WeightUnitsEnum }): number {
  switch (unit) {
    case 'G':
      return value / 1000; // Convert grams to kilograms
    case 'LB':
      return value * 0.453592; // Convert pounds to kilograms
    case 'OZ':
      return value * 0.0283495; // Convert ounces to kilograms
    case 'KG':
      return value; // Already in kilograms
    case 'TONNE':
      return value * 1000; // Convert tonnes to kilograms
    default:
      throw new Error('Unsupported unit');
  }
}

function getWeight({
  product,
  variant,
  productType,
}: {
  product?: { value: number; unit: WeightUnitsEnum } | null;
  variant?: { value: number; unit: WeightUnitsEnum } | null;
  productType?: { value: number; unit: WeightUnitsEnum } | null;
}): number {
  if (variant) return convertToKg(variant);
  if (product) return convertToKg(product);
  if (productType) return convertToKg(productType);

  return 0;
}

export const generateGoogleProductVariant = ({
  product,
  variant,
  channel,
  locale,
  defaultBrand,
  url,
  absoluteify,
  nameGenerator,
  defaultProductType = 'board-game',
  imgUrl,
  productImageUrl,
  brandAttributeSlugs,
  version,
  getProductImage,
  customLabels,
}: {
  product: TProduct;
  variant: TVariant | null;
  channel: TChannel;
  locale: string;
  defaultBrand: string;
  url: (params: { channel: string; locale: string }, args: { slug: string }) => string;
  absoluteify: (url: string) => string;
  nameGenerator?: (props: {
    defaultTitle: string;
    product: TProduct;
    variant: TVariant;
    channel: TChannel;
    locale: string;
  }) => string;
  defaultProductType?: string;
  imgUrl?: TImgUrl;
  productImageUrl?: (params: { channel: string; locale: string }, args: { slug: string }) => string;
  brandAttributeSlugs?: string[];
  version?: string;
  getProductImage?: (product: TProduct, variant?: TVariant | null) => string | null | undefined;
  customLabels?: {
    custom_label_2?: (props: {
      product: TProduct;
      variant: TVariant;
      channel: TChannel;
      locale: string;
    }) => string | null | undefined;
    custom_label_3?: (props: {
      product: TProduct;
      variant: TVariant;
      channel: TChannel;
      locale: string;
    }) => string | null | undefined;
    custom_label_4?: (props: {
      product: TProduct;
      variant: TVariant;
      channel: TChannel;
      locale: string;
    }) => string | null | undefined;
  };
}) => {
  invariant(variant);
  invariant(variant.pricing?.priceUndiscounted?.gross);

  const params = { channel: channel.urlSlug, locale };
  let linkUrl = absoluteify(url(params, { slug: product.slug }));

  // Add variant query param for multi-variant products
  const variantCount = product.variants?.length ?? 0;

  if (variantCount > 1) {
    linkUrl = `${linkUrl}?variant=${variant.id}`;
  }

  const brand = getBrand(product, defaultBrand, brandAttributeSlugs);

  let title = defaultNameGenerator(product, variant);

  if (nameGenerator) {
    title = nameGenerator({ defaultTitle: title, product, variant, channel, locale });
  }

  const gtin =
    getAttributeValue(product.attributes, 'gtin') || getAttributeValue(product.attributes, 'isbn');

  const google_product_category =
    productTypeMapping[product.productType.slug] ||
    productTypeMapping[defaultProductType] ||
    'Toys & Games > Games > Board Games';

  // Use custom product image URL if provided, otherwise fall back to regular image URL
  let image_link: string | undefined;
  let mainImageSourceUrl: string | undefined;

  if (productImageUrl) {
    image_link = absoluteify(productImageUrl(params, { slug: product.slug }));
  } else {
    // Priority: custom handler -> variant media -> product thumbnail
    const customImage = getProductImage ? getProductImage(product, variant) : null;
    const imageUrl = customImage || variant.media?.[0]?.url || product.thumbnail?.url;
    mainImageSourceUrl = imageUrl;

    if (imgUrl && imageUrl) {
      image_link = absoluteify(imgUrl(imageUrl, { width: 1024 }));
    }
  }

  // Append version parameter if provided
  if (image_link && version) {
    const separator = image_link.includes('?') ? '&' : '?';

    image_link = `${image_link}${separator}v=${version}`;
  }

  const shipping_weight = getWeight({
    product: product.weight,
    variant: variant.weight,
    productType: product.productType.weight,
  });

  // Generate additional images - filter out the main image and transform URLs
  const mediaSource = variant.media?.length ? variant.media : product.media;
  const additionalImages = mediaSource
    ?.filter((image) => image.url !== mainImageSourceUrl)
    .map((image) => {
      let imageUrl = image.url;

      // Apply imgUrl transformation if available
      if (imgUrl) {
        imageUrl = absoluteify(imgUrl(image.url, { width: 1024 }));
      }

      // Append version parameter
      if (version) {
        const separator = imageUrl.includes('?') ? '&' : '?';
        imageUrl = `${imageUrl}${separator}v=${version}`;
      }

      return imageUrl;
    })
    .join(',');

  return {
    id: variant.sku || variant.id,
    title,
    description: generateShortDescription(product),
    google_product_category: google_product_category,
    product_type: google_product_category,
    custom_label_0: product.productType.slug,
    custom_label_1: product.category?.slug,
    custom_label_2:
      customLabels?.custom_label_2?.({ product, variant, channel, locale }) ?? undefined,
    custom_label_3:
      customLabels?.custom_label_3?.({ product, variant, channel, locale }) ?? undefined,
    custom_label_4:
      customLabels?.custom_label_4?.({ product, variant, channel, locale }) ?? undefined,
    link: linkUrl,
    image_link,
    shipping_weight: shipping_weight > 0 ? `${shipping_weight} kg` : '',
    condition: 'new',
    availability: product.isAvailable ? 'in_stock' : 'out_of_stock',
    availability_date: '',
    price: variant.pricing?.priceUndiscounted?.gross
      ? formatPrice(variant.pricing?.priceUndiscounted?.gross)
      : null,
    sale_price: variant.pricing?.price?.gross ? formatPrice(variant.pricing?.price?.gross) : null,
    sale_price_effective_date: '',
    brand,
    gtin,
    item_group_id: product.id,
    additional_image_link: additionalImages,
    rich_text_description: product.description
      ? edjsParser.parse(JSON.parse(product.description)).join('')
      : null,
  };
};
